import { Link } from "react-router-dom";
import type { ProjectListItem } from "@/mocks/admin/projects";
import StatusBadge from "./StatusBadge";

type Props = {
  projects: ProjectListItem[];
};

export default function ProjectsTable({ projects }: Props) {
  if (projects.length === 0) {
    return (
      <div className="bg-white border border-ink-100 rounded-lg py-20 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-cream-100 text-ink-400 mx-auto mb-3">
          <i className="ri-inbox-line text-2xl"></i>
        </div>
        <div className="font-display text-lg font-bold text-ink-900">No projects found</div>
        <div className="text-sm text-ink-500 mt-1">Try adjusting your filters or search.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ink-100 rounded-lg overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cream-50 border-b border-ink-100 text-ink-500">
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Project / Customer
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Date & Time
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Route
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Truck / Crew
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Status
              </th>
              <th className="text-right font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Total
              </th>
              <th className="px-5 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr
                key={p.id}
                className="border-b border-ink-100 last:border-b-0 hover:bg-cream-50 transition-colors"
              >
                <td className="px-5 py-4">
                  <Link to={`/projects/${p.id}`} className="flex items-center gap-3 group cursor-pointer">
                    <div
                      className={`w-9 h-9 rounded-full ${p.customerColor} text-white text-xs font-bold flex items-center justify-center shrink-0`}
                    >
                      {p.customerInitials}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] font-bold text-ink-400">{p.id}</span>
                      </div>
                      <div className="text-sm font-semibold text-ink-900 group-hover:text-brand-700 truncate">
                        {p.customer}
                      </div>
                      <div className="text-[11px] text-ink-500 truncate">{p.moveType}</div>
                    </div>
                  </Link>
                </td>
                <td className="px-5 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-ink-900">{p.date}</div>
                  <div className="text-[11px] text-ink-500 mt-0.5">
                    <i className="ri-time-line mr-0.5"></i>
                    {p.time}
                  </div>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-1.5 text-xs text-ink-700">
                    <span className="font-semibold">{p.fromCity}</span>
                    <i className="ri-arrow-right-line text-ink-300"></i>
                    <span className="font-semibold">{p.toCity}</span>
                  </div>
                </td>
                <td className="px-5 py-4 whitespace-nowrap">
                  <div className="text-xs font-semibold text-ink-700">{p.truck}</div>
                  <div className="text-[11px] text-ink-500 mt-0.5">
                    <i className="ri-team-line mr-0.5"></i>
                    {p.crewCount} crew · Lead {p.leadCrew.split(" ")[0]}
                  </div>
                </td>
                <td className="px-5 py-4">
                  <StatusBadge status={p.status} />
                </td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <div className="text-sm font-bold text-ink-900 tabular-nums">
                    ${p.total.toLocaleString()}
                  </div>
                  <div
                    className={`text-[10px] font-semibold mt-0.5 ${
                      p.depositPaid ? "text-emerald-600" : "text-amber-600"
                    }`}
                  >
                    {p.depositPaid ? "Deposit paid" : "Deposit pending"}
                  </div>
                </td>
                <td className="px-5 py-4 text-right">
                  <Link
                    to={`/projects/${p.id}`}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-cream-100 text-ink-500 hover:text-ink-900 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer / pagination */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3 border-t border-ink-100 bg-cream-50">
        <div className="text-xs text-ink-500">
          Showing <span className="font-semibold text-ink-900">{projects.length}</span> of{" "}
          <span className="font-semibold text-ink-900">{projects.length}</span> projects
        </div>
        <div className="flex items-center gap-1">
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-ink-400 hover:bg-cream-100 cursor-pointer">
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-sm font-semibold bg-ink-900 text-white cursor-pointer">
            1
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-sm font-semibold text-ink-500 hover:bg-cream-100 cursor-pointer">
            2
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-sm font-semibold text-ink-500 hover:bg-cream-100 cursor-pointer">
            3
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-ink-500 hover:bg-cream-100 cursor-pointer">
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}